'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn, animations } from '@/styles/variants';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

/** Z-index registry for stacking modals consistently across the app. */
export const MODAL_LAYERS = {
  base: 100,        // default modali
  nested: 200,      // search modali nested in altri modali
  picker: 9999,     // pickers (PortPicker, DateRangePicker)
  critical: 10000,  // BookingConfirm, modali bloccanti
} as const;

interface ModalProps {
  // === core ===
  isOpen: boolean;
  onClose: () => void;
  title?: string | null;
  subtitle?: string | null;
  icon?: React.ReactNode;
  children: React.ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  className?: string;
  // legacy/no-op prop kept for backwards-compat with older callers
  borderColor?: string;

  // === sizing & layout ===
  zIndex?: number;
  bodyClassName?: string;

  // === portal & rendering ===
  usePortal?: boolean;
  portalTarget?: HTMLElement | null;

  // === form integration ===
  asForm?: boolean;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  formId?: string;

  // === content slots ===
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  footerAlign?: 'left' | 'center' | 'right' | 'between';
  footerClassName?: string;
  hideHeaderBorder?: boolean;

  // === UX & accessibility ===
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  lockBodyScroll?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  preventClose?: boolean;
}

// Module-level counter for body scroll lock with nested-modal support.
// Using a singleton counter avoids the "child modal closes → body unlocks
// while parent is still open" bug that you'd get with per-component state.
let lockCount = 0;
let savedBodyOverflow: string | null = null;

function lockBody() {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}

function unlockBody() {
  if (typeof document === 'undefined') return;
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0 && savedBodyOverflow !== null) {
    document.body.style.overflow = savedBodyOverflow;
    savedBodyOverflow = null;
  }
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  '2xl': 'max-w-5xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-[95vw]',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title = null,
  subtitle = null,
  icon = null,
  children,
  size = 'md',
  showCloseButton = true,
  className = '',
  borderColor: _borderColor = 'blue',  // legacy, no-op
  zIndex = MODAL_LAYERS.base,
  bodyClassName,
  usePortal = false,
  portalTarget,
  asForm = false,
  onSubmit,
  formId,
  headerActions,
  footer,
  footerAlign = 'right',
  footerClassName,
  hideHeaderBorder = false,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  lockBodyScroll = true,
  initialFocusRef,
  preventClose = false,
}) => {
  // SSR-safe portal mount flag
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Body scroll lock with nested counter
  useEffect(() => {
    if (!isOpen || !lockBodyScroll) return;
    lockBody();
    return () => unlockBody();
  }, [isOpen, lockBodyScroll]);

  // ESC keyboard handler
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventClose) {
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeOnEsc, preventClose, onClose]);

  // Initial focus
  useEffect(() => {
    if (!isOpen || !initialFocusRef?.current) return;
    // Defer to allow modal to mount + animations to start
    const t = setTimeout(() => initialFocusRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, initialFocusRef]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOverlayClick && !preventClose) {
      onClose();
    }
  };

  const bodyClasses = cn(
    'modal-body p-6 md:p-8 overflow-y-auto flex-1 overscroll-contain bg-white',
    bodyClassName
  );

  // Footer prop → rendered OUTSIDE the scrollable body as a pinned band (always visible).
  // This is different from <ModalFooter> inline (which lives INSIDE the body with negative margins).
  const footerAlignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  }[footerAlign];

  const PinnedFooter = footer ? (
    <div
      data-cid="modal-footer"
      className={cn(
        'modal-footer-band shrink-0',
        'border-t border-slate-200 bg-slate-50/80',
        'px-5 md:px-6 py-3.5',
        'flex items-center gap-3 flex-wrap',
        footerAlignClass,
        footerClassName,
      )}
    >
      {footer}
    </div>
  ) : null;

  const modalJsx = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay — darker + stronger blur for window separation */}
      <div
        className="absolute inset-0 bg-slate-950/65 backdrop-blur-md"
        onClick={handleOverlayClick}
      />

      {/* Window frame — strong shadow + ring + border for elevated chrome */}
      <div
        className={cn(
          'modal-shell',
          'relative w-full overflow-hidden flex flex-col',
          'bg-white rounded-2xl',
          'shadow-[0_25px_60px_-15px_rgba(2,6,23,0.45),0_8px_20px_-8px_rgba(2,6,23,0.25)]',
          'ring-1 ring-slate-900/5 border border-slate-200',
          'max-h-[92vh]',
          sizeClasses[size],
          animations.zoomIn,
          className
        )}
      >
        {/* Title bar — distinct background + border-bottom = window chrome */}
        {(title || showCloseButton || headerActions) && (
          <div
            className={cn(
              'modal-titlebar flex items-center justify-between gap-4 px-5 md:px-6 py-3.5 bg-gradient-to-b from-slate-50 to-slate-100/70 shrink-0',
              !hideHeaderBorder && 'border-b border-slate-200'
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              {icon && (
                <div className="modal-titlebar-icon shrink-0 w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-blue-600">
                  {icon}
                </div>
              )}
              <div className="min-w-0">
                {title && (
                  <h3 className="modal-titlebar-title text-base md:text-lg font-bold text-slate-900 leading-tight truncate">
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="modal-titlebar-subtitle text-xs text-slate-500 leading-tight mt-0.5 truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {headerActions}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={() => !preventClose && onClose()}
                  aria-label="Chiudi"
                  disabled={preventClose}
                  className="modal-titlebar-close shrink-0 w-8 h-8 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 active:scale-95 transition-all flex items-center justify-center text-slate-500 hover:text-slate-900 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Body + Footer. For asForm, <form> wraps both so submit buttons in footer work natively. */}
        {asForm ? (
          <form id={formId} onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
            <div className={bodyClasses}>{children}</div>
            {PinnedFooter}
          </form>
        ) : (
          <>
            <div className={bodyClasses}>{children}</div>
            {PinnedFooter}
          </>
        )}
      </div>
    </div>
  );

  // Portal rendering for top-level modals (e.g. nested deeply but visually
  // need to render at document.body level to escape parent overflow:hidden)
  if (usePortal) {
    if (!mounted) return null;
    return createPortal(modalJsx, portalTarget ?? document.body);
  }

  return modalJsx;
};

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right' | 'between';
}

/**
 * Footer band per i modali. Usa margini negativi per estendersi ai bordi del body
 * (che ha p-6 md:p-8), ottenendo l'aspetto "footer di finestra" senza dover
 * ristrutturare la composizione esistente. Da usare come ULTIMO figlio del Modal,
 * oppure passato via la prop `footer` di Modal (shorthand consigliato).
 */
export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = '',
  align = 'right',
}) => {
  const alignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  }[align];

  return (
    <div
      className={cn(
        'modal-footer-band',
        // Negative margins = break out of body p-6/p-8 padding to reach modal edges
        '-mx-6 md:-mx-8 -mb-6 md:-mb-8 mt-8',
        // Visual: separator + slightly off-white surface = window footer band
        'border-t border-slate-200 bg-slate-50/80',
        'px-5 md:px-6 py-3.5',
        // Bottom rounding to match modal corners
        'rounded-b-2xl',
        // Layout
        'flex items-center gap-3 flex-wrap',
        alignClass,
        className
      )}
    >
      {children}
    </div>
  );
};

export default Modal;
